<!-- (c) TwistedMinds, Gerry Uytdenhouwen - 3 november 2026 -->
<template>
    <div class="orders-toolbar-v2">
        <div class="orders-toolbar-v2__group orders-toolbar-v2__group--chips">
            <span class="orders-toolbar-v2__label">{{ $t('general.select-heading') }}</span>
            <div class="orders-toolbar-v2__chips">
                <v-tooltip bottom v-for="key in optionKeys" :key="key">
                    <template v-slot:activator="{ on, attrs }">
                        <button
                            type="button"
                            class="orders-toolbar-v2__chip"
                            :class="{'orders-toolbar-v2__chip--on': selectOptions.includes(key)}"
                            :aria-pressed="selectOptions.includes(key) ? 'true' : 'false'"
                            v-bind="attrs"
                            v-on="on"
                            @click="$emit('select-options', toggle(selectOptions, key))">
                            <v-icon v-if="selectOptions.includes(key)" x-small>mdi-check</v-icon>
                            {{ $t(`general.select-${key.replace(/_/g, '-')}`) }}
                        </button>
                    </template>
                    <span>{{ $t(`general.tt_filter_${key}`) }}</span>
                </v-tooltip>
            </div>
        </div>

        <div class="orders-toolbar-v2__group orders-toolbar-v2__group--actions">
            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <span class="orders-toolbar-v2__count" v-bind="attrs" v-on="on">
                        {{ $t('general.select-count', {count: selected.length}) }}
                    </span>
                </template>
                <span>{{ $t('general.tt_select_count') }}</span>
            </v-tooltip>
            <span class="orders-toolbar-v2__separator"></span>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <span class="orders-toolbar-v2__tip" v-bind="attrs" v-on="on">
                        <v-btn
                            small
                            depressed
                            class="orders-toolbar-v2__button"
                            :disabled="noSelection"
                            :loading="loadingPrint"
                            @click="$emit('print-labels', selected)">
                            <v-icon left small>mdi-printer</v-icon>
                            {{ $t('general.print-labels-v2') }}
                        </v-btn>
                    </span>
                </template>
                <span>{{ $t('general.tt_filter_labels_print') }}</span>
            </v-tooltip>

            <download-excel
                :data="selected"
                :fields="excelFields"
                :name="todayDate"
            >
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <span class="orders-toolbar-v2__tip" v-bind="attrs" v-on="on">
                            <v-btn
                                small
                                depressed
                                class="orders-toolbar-v2__button"
                                :disabled="noSelection">
                                <v-icon left small>mdi-download</v-icon>
                                {{ $t('general.excel-short') }}
                            </v-btn>
                        </span>
                    </template>
                    <span>{{ $t('general.tt_filter_excel') }}</span>
                </v-tooltip>
            </download-excel>

            <v-menu v-model="menuOpen" offset-y left>
                <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                    <v-tooltip bottom :disabled="menuOpen">
                        <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                            <span class="orders-toolbar-v2__tip" v-bind="tooltipAttrs" v-on="tooltipOn">
                                <v-btn
                                    small
                                    depressed
                                    class="orders-toolbar-v2__button"
                                    :disabled="noSelection"
                                    v-bind="menuAttrs"
                                    v-on="menuOn">
                                    {{ $t('general.more') }}
                                    <v-icon right small>mdi-menu-down</v-icon>
                                </v-btn>
                            </span>
                        </template>
                        <span>{{ $t('general.tt_filter_more') }}</span>
                    </v-tooltip>
                </template>
                <v-list dense>
                    <v-tooltip left>
                        <template v-slot:activator="{ on, attrs }">
                            <v-list-item v-bind="attrs" v-on="on" @click="$emit('mail-labels', selected)">
                                <v-list-item-icon class="mr-3">
                                    <v-icon small>mdi-email-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>{{ $t('general.mail-action') }}</v-list-item-title>
                            </v-list-item>
                        </template>
                        <span>{{ $t('general.tt_filter_labels_mail') }}</span>
                    </v-tooltip>
                    <v-tooltip left>
                        <template v-slot:activator="{ on, attrs }">
                            <v-list-item v-bind="attrs" v-on="on" @click="dialogDelete = true">
                                <v-list-item-icon class="mr-3">
                                    <v-icon small color="#c0392b">mdi-delete</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title class="orders-toolbar-v2__delete">
                                    {{ $t('general.delete-action') }}
                                </v-list-item-title>
                            </v-list-item>
                        </template>
                        <span>{{ $t('general.tt_filter_labels_delete') }}</span>
                    </v-tooltip>
                </v-list>
            </v-menu>
        </div>

        <delete-selected-dialog v-model="dialogDelete" :selected="selected" @done="$emit('deleted')"/>
    </div>
</template>

<script>
import {ORDER_EXCEL_FIELDS} from "@/utils/orderExcel";
import {SELECT_OPTIONS, toggleOption} from "@/utils/orderSelection";
import DeleteSelectedDialog from "./DeleteSelectedDialog";

// Selection chips, counter and actions that sit inside the orders table card
export default {
    name: "OrdersToolbarV2",
    components: {DeleteSelectedDialog},
    props: {
        selected: {type: Array, default: () => []},
        // keys of the chips that are switched on
        selectOptions: {type: Array, default: () => []},
        loadingPrint: {type: Boolean, default: false},
    },
    data: () => ({
        optionKeys: SELECT_OPTIONS,
        dialogDelete: false,
        menuOpen: false,
        excelFields: ORDER_EXCEL_FIELDS,
        todayDate: `${new Date().toString()}.xls`,
    }),
    computed: {
        noSelection() {
            return !this.selected.length;
        },
    },
    methods: {
        toggle: toggleOption,
    },
}
</script>

<style lang="scss">
.orders-toolbar-v2 {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 12px;

    .orders-toolbar-v2__group {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 10px;
        min-width: 0;
    }

    // the actions stay at the right end of the row
    .orders-toolbar-v2__group--actions {
        margin-left: auto;
    }

    .orders-toolbar-v2__label {
        color: #6b7a80;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: .05em;
        text-transform: uppercase;
        white-space: nowrap;
    }

    .orders-toolbar-v2__chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }

    .orders-toolbar-v2__chip {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 4px 11px;
        border: 1px solid #e8ecee;
        border-radius: 999px;
        background: transparent;
        color: #5d6b70;
        font-size: 12px;
        font-weight: 600;
        white-space: nowrap;
        cursor: pointer;

        .v-icon {
            color: inherit;
        }
    }

    .orders-toolbar-v2__chip--on {
        background: #e5eef9;
        border-color: #e5eef9;
        color: #2b5fa8;
    }

    .orders-toolbar-v2__count {
        color: #1d6f86;
        font-weight: 700;
        white-space: nowrap;
    }

    .orders-toolbar-v2__separator {
        width: 1px;
        height: 24px;
        background: #e8ecee;
    }

    // wrapper that carries the hover listeners; stays a single flex item
    .orders-toolbar-v2__tip {
        display: inline-flex;
    }

    .v-btn.orders-toolbar-v2__button {
        background-color: #f2f4f5 !important;
        border-radius: 8px;
        color: #5d6b70;
        font-weight: 600;
        letter-spacing: normal;
        text-transform: none;

        &.v-btn--disabled {
            opacity: .4;
            color: #5d6b70 !important;
        }
    }
}

// the menu is rendered outside the toolbar element
.orders-toolbar-v2__delete {
    color: #c0392b;
}
</style>
